/**
 * 국가 목록 유틸리티
 * - 백엔드 API(/country/list, /country/calling-code/list) 데이터만 사용
 */
import { normalizeLocaleTag, resolveIntlLocale } from '@/lib/i18nLocale';

export interface CountryOption {
    countryId: number;
    /** ISO 3166-1 alpha-2 코드 (KR, US ...) — 백엔드 code 필드 */
    alpha2: string;
    title: string;
    /** 해당 국가의 기본 전화코드 ID */
    defaultCountryCallingCodeId?: number;
}

export interface CountryCallingCodeOption {
    countryCallingCodeId: number;
    /** +82, +1 ... */
    code: string;
    /** alpha-2 코드 (KR, US ...) — 백엔드 title 필드 */
    title: string;
}

type RegionDisplayNamesConstructor = {
    new (
        locales?: string | string[],
        options?: { type: 'region' },
    ): { of: (code: string) => string | undefined };
    supportedLocalesOf: (locales: string | string[]) => string[];
};

function getRegionDisplayName(alpha2: string, locale: string | undefined): string {
    if (!alpha2 || normalizeLocaleTag(locale) === 'ko') return '';
    const displayNamesCtor = (Intl as typeof Intl & {
        DisplayNames?: RegionDisplayNamesConstructor;
    }).DisplayNames;
    if (!displayNamesCtor) return '';
    const intlLocale = resolveIntlLocale(locale);
    if (displayNamesCtor.supportedLocalesOf(intlLocale).length === 0) return '';
    try {
        return new displayNamesCtor(intlLocale, { type: 'region' }).of(alpha2) ?? '';
    } catch {
        return '';
    }
}

function getCountryTitle(
    country: { title: string; titleEn?: string; code?: string; alpha2?: string },
    locale?: string,
): string {
    const alpha2 = (country.code ?? country.alpha2 ?? '').toUpperCase();
    const localizedTitle = getRegionDisplayName(alpha2, locale);
    if (localizedTitle) return localizedTitle;
    if (normalizeLocaleTag(locale) !== 'ko' && country.titleEn) return country.titleEn;
    return country.title;
}

/** 대한민국을 맨 앞에, 나머지는 현재 표시 언어 기준 정렬 */
function sortCountries(list: CountryOption[], locale?: string): CountryOption[] {
    const kr = list.find((c) => c.alpha2 === 'KR' || c.countryId === 82);
    const intlLocale = resolveIntlLocale(locale);
    const rest = list
        .filter((c) => c !== kr)
        .sort((a, b) => a.title.localeCompare(b.title, intlLocale));
    return kr ? [kr, ...rest] : rest;
}

/** 백엔드 /country/list 응답 → CountryOption[] */
export function toCountryOptions(
    apiList: { countryId: number; title: string; titleEn?: string; code?: string; alpha2?: string; defaultCountryCallingCodeId?: number }[],
    locale?: string,
): CountryOption[] {
    return sortCountries(
        apiList.map((c) => ({
            countryId: c.countryId,
            alpha2: (c.code ?? c.alpha2 ?? '').toUpperCase(),
            title: getCountryTitle(c, locale),
            defaultCountryCallingCodeId: c.defaultCountryCallingCodeId,
        })),
        locale,
    );
}

/** 백엔드 /country/calling-code/list 응답 → CountryCallingCodeOption[] */
export function toCallingCodeOptions(
    apiList: { countryCallingCodeId: number; code: string; title: string }[]
): CountryCallingCodeOption[] {
    const list = apiList.map((c) => ({
        countryCallingCodeId: c.countryCallingCodeId,
        code: c.code.startsWith('+') ? c.code : `+${c.code}`,
        title: (c.title ?? '').trim().toUpperCase(),
    }));
    // +82 (KR) 맨 앞, 나머지는 코드 숫자 오름차순
    const kr = list.find((c) => c.title === 'KR' || c.code === '+82');
    const rest = list
        .filter((c) => c !== kr)
        .sort((a, b) => {
            const na = parseInt(a.code.replace('+', ''), 10);
            const nb = parseInt(b.code.replace('+', ''), 10);
            return na - nb;
        });
    return kr ? [kr, ...rest] : rest;
}

/** countryId로 alpha2 조회 */
export function getAlpha2ByCountryId(
    countryId: number,
    countryOptions: CountryOption[]
): string | undefined {
    return countryOptions.find((c) => c.countryId === countryId)?.alpha2;
}
